import React from 'react';
import { Input, Select, Textarea } from '@fluentui/react-components';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import chevronIcon from './assets/chevron-right.svg';
import { resolveWorkspaceCustomerStageOptions } from '../../utils/projectMetadataOptions';
import { useSelector } from 'react-redux';
import DocumentContextBanner from './DocumentContextBanner';
import { ensureHttpsWebsite, isWebsiteUrl } from '../../utils/websiteUrl';

export const SECTOR_OPTIONS = [
    'Software & Technology',
    'Financial Services',
    'Healthcare',
    'Manufacturing',
    'Retail & Consumer',
    'Telecommunications',
    'Energy & Utilities',
    'Professional Services',
    'Media & Entertainment',
    'Education',
    'Other',
];

export const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];

export const BUSINESS_MODEL_OPTIONS = ['B2B', 'B2C', 'B2B2C', 'Marketplace', 'Other'];

export const CUSTOMER_SEGMENT_OPTIONS = ['Enterprise', 'Mid-market', 'SME', 'Consumer', 'Public Sector', 'Mixed', 'Other'];

export const CURRENCY_OPTIONS = ['GBP', 'USD', 'EUR'];

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/svg+xml'];

const ProvenanceBadge = ({ source }) => {
    if (!source) return null;
    return <span className={styles.provenanceBadge}>Found · {source}</span>;
};

const FieldLabel = ({ children, required, source, split }) => (
    <div className={split ? styles.fieldLabelSplit : styles.fieldLabelRow}>
        <p className={styles.fieldLabel}>
            {children}
            {required ? <span className={styles.required}> *</span> : null}
        </p>
        <ProvenanceBadge source={source} />
    </div>
);

const CreateProjectStep3 = ({
    formData,
    onChange,
    onContinue,
    onBack,
    continueLoading,
    documentBanner,
    onDismissDocumentBanner,
    onRefreshRecommendations,
    refreshingRecommendations = false,
}) => {
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');
    const fileInputRef = React.useRef(null);
    const [additionalOpen, setAdditionalOpen] = React.useState(false);

    const activeTenant = React.useMemo(() => {
        const membership = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return membership?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const companyStageOptions = React.useMemo(
        () => resolveWorkspaceCustomerStageOptions(activeTenant, { includeValue: formData?.customer_stage }),
        [activeTenant, formData?.customer_stage],
    );

    const fieldMeta = formData?.fieldMeta || {};
    const handleChange = e => onChange({ [e.target.name]: e.target.value });
    const handleWebsiteBlur = () => {
        const next = ensureHttpsWebsite(formData.website);
        if (next && next !== formData.website && isWebsiteUrl(next)) {
            onChange({ website: next });
        }
    };

    const processLogo = file => {
        const isSvgByName = file?.name && file.name.toLowerCase().endsWith('.svg');
        if (!file || !(IMAGE_TYPES.includes(file.type) || (isSvgByName && (!file.type || file.type === 'image/svg+xml')))) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => onChange({ logo: reader.result, logo_name: file.name });
        reader.readAsDataURL(file);
    };

    const handleLogoChange = e => {
        const file = e.target.files?.[0];
        if (file) processLogo(file);
        e.target.value = '';
    };

    const showWebsiteError = formData?.website?.trim() && !isWebsiteUrl(formData.website);
    const continueDisabled = !formData?.company_name?.trim() || !formData?.sector || !formData?.description?.trim() || showWebsiteError;
    const additionalCount = Array.isArray(formData?.additional_items) ? formData.additional_items.length : 0;

    return (
        <NewProjectWizardCard
            step={3}
            wide
            title="Review organisation"
            subtitle="Lens has populated what it could find. Review or edit anything that needs changing."
            continueLabel="Confirm & continue"
            continueDisabled={continueDisabled}
            continueLoading={continueLoading}
            onContinue={onContinue}
            onBack={onBack}
            backInline>
            {documentBanner ? (
                <DocumentContextBanner
                    variant={documentBanner}
                    onDismiss={onDismissDocumentBanner}
                    onRefresh={onRefreshRecommendations}
                    refreshing={refreshingRecommendations}
                    onNotNow={onDismissDocumentBanner}
                />
            ) : null}
            <div className={styles.sectionDivider}>
                <span className={styles.sectionTitle}>Company profile</span>
                <span className={styles.sectionLine} />
            </div>

            <div className={styles.formGrid}>
                <div className={styles.fieldBlock}>
                    <FieldLabel required>Organisation Name</FieldLabel>
                    <Input className={styles.textInput} name="company_name" value={formData.company_name || ''} onChange={handleChange} required />
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel>Website</FieldLabel>
                    <Input
                        className={styles.textInput}
                        name="website"
                        type="url"
                        value={formData.website || ''}
                        onChange={handleChange}
                        onBlur={handleWebsiteBlur}
                        placeholder="https://"
                    />
                    {showWebsiteError ? <span className={styles.errorMessage}>Please enter a URL in https:// or http:// format</span> : null}
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel required source={fieldMeta.sector?.source}>
                        Sector / Industry
                    </FieldLabel>
                    <Select className={styles.selectField} name="sector" value={formData.sector || ''} onChange={handleChange} required>
                        <option value="">Select</option>
                        {SECTOR_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                        {formData.sector && !SECTOR_OPTIONS.includes(formData.sector) ? (
                            <option value={formData.sector}>{formData.sector}</option>
                        ) : null}
                    </Select>
                </div>
                <div className={styles.fieldBlockFull}>
                    <FieldLabel required source={fieldMeta.description?.source}>
                        Company Description
                    </FieldLabel>
                    <Textarea
                        className={styles.textareaInput}
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        rows={4}
                        resize="none"
                        required
                    />
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.company_size?.source}>Company Size</FieldLabel>
                    <Select className={styles.selectField} name="company_size" value={formData.company_size || ''} onChange={handleChange}>
                        <option value="">Select</option>
                        {COMPANY_SIZE_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                        {formData.company_size && !COMPANY_SIZE_OPTIONS.includes(formData.company_size) ? (
                            <option value={formData.company_size}>{formData.company_size}</option>
                        ) : null}
                    </Select>
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.established_year?.source}>Year Established</FieldLabel>
                    <Input className={styles.textInput} name="established_year" value={formData.established_year || ''} onChange={handleChange} />
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.customer_stage?.source}>Company Stage</FieldLabel>
                    <Select className={styles.selectField} name="customer_stage" value={formData.customer_stage || ''} onChange={handleChange}>
                        <option value="">Select</option>
                        {companyStageOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.text}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.customer_type?.source}>Business Model</FieldLabel>
                    <Select className={styles.selectField} name="customer_type" value={formData.customer_type || ''} onChange={handleChange}>
                        <option value="">Select</option>
                        {BUSINESS_MODEL_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.customer_segment?.source}>Primary Customer Segment</FieldLabel>
                    <Select className={styles.selectField} name="customer_segment" value={formData.customer_segment || ''} onChange={handleChange}>
                        <option value="">Select</option>
                        {CUSTOMER_SEGMENT_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className={styles.fieldBlock}>
                    <FieldLabel>Revenue</FieldLabel>
                    <div className={styles.revenueRow}>
                        <Select className={styles.currencySelect} name="currency" value={formData.currency || 'GBP'} onChange={handleChange}>
                            {CURRENCY_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                        <Input className={styles.textInput} name="revenue" value={formData.revenue || ''} onChange={handleChange} placeholder="Amount" />
                    </div>
                </div>
            </div>

            <div className={styles.sectionDivider}>
                <span className={styles.sectionTitle}>Brand & identifiers</span>
                <span className={styles.sectionLine} />
            </div>

            <div className={styles.formGrid}>
                <div className={styles.fieldBlock}>
                    <FieldLabel source={fieldMeta.logo?.source} split>
                        Logo
                    </FieldLabel>
                    <div className={styles.logoBox}>
                        {formData.logo ? (
                            <img src={formData.logo} alt="Organisation logo" className={styles.logoPreview} />
                        ) : (
                            <span className={styles.logoEmpty}>No logo found</span>
                        )}
                    </div>
                    <div className={styles.logoActions}>
                        <button type="button" className={styles.logoChange} onClick={() => fileInputRef.current?.click()}>
                            Change
                        </button>
                        <button type="button" className={styles.logoClear} onClick={() => onChange({ logo: '', logo_name: '' })}>
                            Clear
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                            className={styles.hiddenFileInput}
                            onChange={handleLogoChange}
                        />
                    </div>
                </div>
                <div className={styles.brandFields}>
                    <div className={styles.fieldBlock}>
                        <FieldLabel source={fieldMeta.linkedin?.source}>LinkedIn Company Page</FieldLabel>
                        <Input
                            className={styles.textInput}
                            name="linkedin"
                            value={formData.linkedin || ''}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/company/..."
                        />
                    </div>
                    <div className={styles.fieldBlock}>
                        <FieldLabel>Product Website</FieldLabel>
                        <Input className={styles.textInput} name="product_website" value={formData.product_website || ''} onChange={handleChange} placeholder="https://" />
                    </div>
                    <div className={styles.fieldBlock}>
                        <FieldLabel source={fieldMeta.companieshouse?.source}>Companies House ID</FieldLabel>
                        <Input className={styles.textInput} name="companieshouse" value={formData.companieshouse || ''} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </NewProjectWizardCard>
    );
};

export default CreateProjectStep3;
